import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, Table2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { ColumnDef } from '@/hooks/useReportBuilder';

interface Props {
  columns: ColumnDef[];
  selectedCols: string[];
  rows: Record<string, unknown>[];
  loading: boolean;
  error: string | null;
}

function formatCell(value: unknown, type: string): string {
  if (value === null || value === undefined) return '—';
  if (type === 'date' && typeof value === 'string') {
    try {
      return new Date(value).toLocaleDateString('pt-BR');
    } catch { return value; }
  }
  if (type === 'number' && typeof value === 'number') {
    return value.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  }
  return String(value);
}

export const PreviewTable: React.FC<Props> = ({ columns, selectedCols, rows, loading, error }) => {
  const visibleCols = columns.filter(c => selectedCols.includes(c.key));

  if (selectedCols.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
        <Table2 className="h-10 w-10 mb-3 opacity-30" />
        <p className="text-sm">Selecione pelo menos uma coluna para pré-visualizar.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        {error}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-8 w-full" />)}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground text-sm">
        <Table2 className="h-8 w-8 mb-2 opacity-30" />
        <p>Nenhum dado encontrado com os filtros atuais.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Pré-visualização (máx. 50 linhas) — <strong>{rows.length}</strong> resultado(s)
        </p>
      </div>
      <ScrollArea className="rounded-md border max-h-80">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleCols.map(c => (
                <TableHead key={c.key} className="text-xs whitespace-nowrap">{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i} className="text-xs">
                {visibleCols.map(c => (
                  <TableCell key={c.key} className="whitespace-nowrap py-1.5 max-w-[200px] truncate">
                    {formatCell(row[c.key], c.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};
